import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Business as IceIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  SlideUpTransition,
  useMinimizable,
  MinimizeButton,
  MinimizedBar,
} from '../../components/MinimizableDialog';
import { Facture, FactureCreate, FactureUpdate, FactureItemCreate, DevisAcompteInfo, AvailableAcompte } from '../../types/facture';
import { Client } from '../../types/client';
import { Devis } from '../../types/devis';
import { BonLivraison } from '../../types/bonLivraison';
import { facturesAPI, clientsAPI, bonsLivraisonAPI, devisAPI } from '../../services/api';
import { Warning as WarningIcon } from '@mui/icons-material';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import useFormDraft from '../../hooks/useFormDraft';
import { extractErrorMessage } from '../../utils/errorHandler';
import { useClientPlafond } from '../../hooks/useClientPlafond';

interface LineItem {
  bon_livraison_item_id?: number;
  designation: string;
  quantite: number;
  prix_unitaire: number | null | string;
  total: number;
}

interface FactureFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  factureId?: number;
  factureType?: 'normale' | 'acompte';
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

const TVA_OPTIONS = [
  { value: 0, label: '0%' },
  { value: 7, label: '7%' },
  { value: 10, label: '10%' },
  { value: 14, label: '14%' },
  { value: 20, label: '20%' },
];

const FactureFormDialog: React.FC<FactureFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  factureId,
  factureType = 'normale',
  minimized: controlledMinimized,
  onMinimizedChange,
}) => {
  const isEdit = !!factureId;
  const isAcompte = factureType === 'acompte';

  const [loading, setLoading] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );
  const [clients, setClients] = useState<Client[]>([]);
  const [allBonsLivraison, setAllBonsLivraison] = useState<BonLivraison[]>([]);
  const [availableBLs, setAvailableBLs] = useState<BonLivraison[]>([]);

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedBLs, setSelectedBLs] = useState<BonLivraison[]>([]);

  // Acompte deduction state (for normal factures)
  const [availableAcomptes, setAvailableAcomptes] = useState<AvailableAcompte[]>([]);
  const [selectedAcompteIds, setSelectedAcompteIds] = useState<number[]>([]);

  // Acompte-specific state
  const [devisList, setDevisList] = useState<Devis[]>([]);
  const [selectedDevis, setSelectedDevis] = useState<Devis | null>(null);
  const [acomptePercentage, setAcomptePercentage] = useState<number | string>('');
  const [acompteBcNumero, setAcompteBcNumero] = useState<string>('');
  const [acompteInfo, setAcompteInfo] = useState<DevisAcompteInfo | null>(null);

  // Use reusable hook for plafond check
  const { clientSummary, isPlafondDepasse } = useClientPlafond(selectedClient?.id);

  const [formData, setFormData] = useState<{
    date: string;
    remarque: string;
    tva_rate: number;
    remise: number | null | string;
  }>({
    date: new Date().toISOString().split('T')[0],
    remarque: '',
    tva_rate: 20,
    remise: null,
  });

  const [lineItems, setLineItems] = useState<LineItem[]>([]);

  const { alertState, showError, showSuccess, hideAlert } = useAlert();

  // Draft caching — only for new factures (never when editing an existing row).
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    formData: typeof formData;
    selectedClient: Client | null;
    selectedBLs: BonLivraison[];
    selectedAcompteIds: number[];
    selectedDevis: Devis | null;
    acomptePercentage: number | string;
    acompteBcNumero: string;
    lineItems: LineItem[];
  }>('facture', !isEdit);

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      remarque: '',
      tva_rate: 20,
      remise: null,
    });
    setSelectedClient(null);
    setSelectedBLs([]);
    setLineItems([]);
    setAvailableBLs([]);
    setSelectedDevis(null);
    setAcomptePercentage('');
    setAcompteBcNumero('');
    setAcompteInfo(null);
    setAvailableAcomptes([]);
    setSelectedAcompteIds([]);
  };

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        try {
          const clientsData = await clientsAPI.getAllForDropdown();
          setClients(clientsData);

          let allDevis: Devis[] = [];
          if (isAcompte) {
            allDevis = await devisAPI.getAllForDropdown();
            setDevisList(allDevis);
          }

          if (isEdit && factureId) {
            const promises: Promise<any>[] = [facturesAPI.getById(factureId)];
            if (!isAcompte) {
              promises.push(bonsLivraisonAPI.getAll());
            }
            const results = await Promise.all(promises);
            const facture = results[0];
            const blsData = results[1] || [];
            setAllBonsLivraison(blsData);
            populateForm(facture, clientsData, blsData, allDevis);
          } else {
            const draft = loadDraft();
            if (draft) {
              setFormData(draft.formData);
              setSelectedClient(draft.selectedClient);
              setSelectedBLs(draft.selectedBLs);
              setSelectedAcompteIds(draft.selectedAcompteIds);
              setSelectedDevis(draft.selectedDevis);
              setAcomptePercentage(draft.acomptePercentage);
              setAcompteBcNumero(draft.acompteBcNumero);
              setLineItems(draft.lineItems);
            } else {
              resetForm();
            }
          }
        } catch (err) {
          showError('Erreur lors du chargement des données');
          console.error(err);
        }
      };
      fetchData();
    }
  }, [open, factureId, isEdit, isAcompte]);

  // Fetch available BLs from backend when client is selected (normale mode only)
  useEffect(() => {
    if (selectedClient && !isAcompte) {
      const fetchAvailableBLs = async () => {
        try {
          const availableBLsData = await bonsLivraisonAPI.getAvailableForClient(
            selectedClient.id,
            isEdit ? factureId : undefined
          );
          setAvailableBLs(availableBLsData);
        } catch (err) {
          console.error('Error fetching available BLs:', err);
          setAvailableBLs([]);
        }
      };
      fetchAvailableBLs();
    } else if (!isAcompte) {
      setAvailableBLs([]);
    }
  }, [selectedClient, isEdit, factureId, isAcompte]);

  // Fetch available acomptes when client is selected (normale mode only)
  useEffect(() => {
    if (selectedClient && !isAcompte) {
      const fetchAvailableAcomptes = async () => {
        try {
          const acomptes = await facturesAPI.getAvailableAcomptes(
            selectedClient.id,
            isEdit ? factureId : undefined
          );
          setAvailableAcomptes(acomptes);
        } catch (err) {
          console.error('Error fetching available acomptes:', err);
          setAvailableAcomptes([]);
        }
      };
      fetchAvailableAcomptes();
    } else if (!isAcompte) {
      setAvailableAcomptes([]);
      setSelectedAcompteIds([]);
    }
  }, [selectedClient, isEdit, factureId, isAcompte]);

  // Fetch acompte info when devis is selected
  useEffect(() => {
    if (selectedDevis && isAcompte) {
      const fetchAcompteInfo = async () => {
        try {
          const info = await facturesAPI.getDevisAcompteInfo(selectedDevis.id);
          setAcompteInfo(info);
        } catch (err) {
          console.error('Error fetching acompte info:', err);
          setAcompteInfo(null);
        }
      };
      fetchAcompteInfo();
    } else {
      setAcompteInfo(null);
    }
  }, [selectedDevis, isAcompte]);

  const populateForm = (
    facture: Facture,
    clientsList: Client[],
    blsList: BonLivraison[],
    fetchedDevisList: Devis[] = []
  ) => {
    setFormData({
      date: facture.date,
      remarque: facture.remarque || '',
      tva_rate: Number(facture.tva_rate),
      remise: facture.remise,
    });

    const client = clientsList.find(c => c.id === facture.client_id);
    setSelectedClient(client || null);

    if (facture.type === 'acompte') {
      // Populate acompte fields
      setAcomptePercentage(Number(facture.acompte_percentage) || '');
      setAcompteBcNumero(facture.bc_numero || '');

      // Find and set the devis (use passed list, not stale state)
      if (facture.devis_id) {
        const devis = fetchedDevisList.find(d => d.id === facture.devis_id);
        setSelectedDevis(devis || null);
      }

      // Populate free-form items
      if (facture.items && facture.items.length > 0) {
        setLineItems(
          facture.items.map(item => ({
            designation: item.designation,
            quantite: Number(item.quantite),
            prix_unitaire: Number(item.prix_unitaire),
            total: Number(item.total),
          }))
        );
      }
    } else {
      // Normal facture - populate BLs
      const bls = facture.bons_livraison.map(factureBl => {
        const fullBl = blsList.find(bl => bl.id === factureBl.id);
        return fullBl || factureBl as unknown as BonLivraison;
      });
      setSelectedBLs(bls);

      if (facture.items && facture.items.length > 0) {
        setLineItems(
          facture.items.map(item => ({
            bon_livraison_item_id: item.bon_livraison_item_id,
            designation: item.designation,
            quantite: Number(item.quantite),
            prix_unitaire: Number(item.prix_unitaire),
            total: Number(item.total),
          }))
        );
      }

      // Pre-select acompte deductions
      if (facture.acompte_deductions && facture.acompte_deductions.length > 0) {
        setSelectedAcompteIds(facture.acompte_deductions.map(d => d.acompte_facture_id));
      }
    }
  };

  const handleClientChange = (client: Client | null) => {
    setSelectedClient(client);
    setSelectedBLs([]);
    setLineItems([]);
    setSelectedAcompteIds([]);
  };

  // Helper: build line items from devis items, applying acompte percentage to prix_unitaire
  const buildAcompteItems = (devis: Devis, pct: number) => {
    if (!devis.items || devis.items.length === 0) return [];
    const factor = pct > 0 ? pct / 100 : 1;
    return devis.items.map(item => {
      const prix = Math.round(Number(item.prix_unitaire) * factor * 100) / 100;
      const qty = Number(item.quantite);
      return {
        designation: item.designation,
        quantite: qty,
        prix_unitaire: prix,
        total: Math.round(qty * prix * 100) / 100,
      };
    });
  };

  const handleDevisChange = (devis: Devis | null) => {
    setSelectedDevis(devis);
    if (devis) {
      // Auto-fill client from devis
      const client = clients.find(c => c.id === devis.client_id);
      setSelectedClient(client || null);

      // Pre-fill items from devis articles (at full price initially)
      if (devis.items && devis.items.length > 0) {
        setLineItems(
          devis.items.map(item => ({
            designation: item.designation,
            quantite: Number(item.quantite),
            prix_unitaire: Number(item.prix_unitaire),
            total: Math.round(Number(item.quantite) * Number(item.prix_unitaire) * 100) / 100,
          }))
        );
      } else {
        setLineItems([]);
      }
    } else {
      setSelectedClient(null);
      setLineItems([]);
    }
    setAcomptePercentage('');
  };

  const handleAddBL = (bl: BonLivraison | null) => {
    if (bl && !selectedBLs.find(s => s.id === bl.id)) {
      setSelectedBLs([...selectedBLs, bl]);

      // Add items from this BL
      if (bl.items && bl.items.length > 0) {
        const newItems: LineItem[] = bl.items.map(blItem => ({
          bon_livraison_item_id: blItem.id,
          designation: blItem.designation,
          quantite: Number(blItem.quantite),
          prix_unitaire: null,
          total: 0,
        }));
        setLineItems([...lineItems, ...newItems]);
      }
    }
  };

  const handleRemoveBL = (blId: number) => {
    const bl = selectedBLs.find(b => b.id === blId);
    if (bl) {
      setSelectedBLs(selectedBLs.filter(b => b.id !== blId));

      // Remove items from this BL
      const blItemIds = bl.items?.map(item => item.id) || [];
      setLineItems(lineItems.filter(item => !blItemIds.includes(item.bon_livraison_item_id || 0)));
    }
  };

  // Acompte: add/remove free-form line items
  const handleAddAcompteItem = () => {
    setLineItems([...lineItems, { designation: '', quantite: 1, prix_unitaire: null, total: 0 }]);
  };

  const handleRemoveAcompteItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const handleAcompteItemChange = (index: number, field: string, value: string) => {
    const newItems = [...lineItems];
    if (field === 'designation') {
      newItems[index].designation = value;
    } else if (field === 'quantite') {
      if (value === '' || /^\d*[.,]?\d*$/.test(value)) {
        const numVal = parseFloat(value.replace(',', '.')) || 0;
        newItems[index].quantite = numVal;
        const prix = typeof newItems[index].prix_unitaire === 'string'
          ? parseFloat((newItems[index].prix_unitaire as string).replace(',', '.')) || 0
          : (newItems[index].prix_unitaire ?? 0);
        newItems[index].total = Math.round(numVal * Number(prix) * 100) / 100;
      }
    }
    setLineItems(newItems);
  };

  const handlePrixUnitaireChange = (index: number, value: string) => {
    const newItems = [...lineItems];

    if (value === '') {
      newItems[index].prix_unitaire = null;
      newItems[index].total = 0;
    } else if (/^\d*[.,]?\d*$/.test(value)) {
      newItems[index].prix_unitaire = value;
      const prix = parseFloat(value.replace(',', '.')) || 0;
      newItems[index].total = Math.round(newItems[index].quantite * prix * 100) / 100;
    }

    setLineItems(newItems);
  };

  const handlePrixUnitaireBlur = (index: number) => {
    const newItems = [...lineItems];
    const value = newItems[index].prix_unitaire;

    if (value && value !== '') {
      const numValue = typeof value === 'string'
        ? parseFloat(value.replace(',', '.'))
        : value;

      if (!isNaN(numValue)) {
        newItems[index].prix_unitaire = numValue;
        newItems[index].total = Math.round(newItems[index].quantite * numValue * 100) / 100;
      } else {
        newItems[index].prix_unitaire = null;
        newItems[index].total = 0;
      }
      setLineItems(newItems);
    }
  };

  // 1. Total HT
  const calculateTotalHT = () => {
    const total = lineItems.reduce((sum, item) => sum + (item.total || 0), 0);
    return Math.round(total * 100) / 100;
  };

  // 2. Remise on HT (not on TTC)
  const calculateRemiseAmount = () => {
    const remise = typeof formData.remise === 'string'
      ? parseFloat(formData.remise.replace(',', '.')) || 0
      : (formData.remise ?? 0);
    const remiseAmount = calculateTotalHT() * (remise / 100);
    return Math.round(remiseAmount * 100) / 100;
  };

  // 3. Net commercial = HT - Remise
  const calculateNetCommercial = () => {
    return Math.round((calculateTotalHT() - calculateRemiseAmount()) * 100) / 100;
  };

  // 4. TVA on net commercial (not on original HT)
  const calculateTVAAmount = () => {
    const tva = calculateNetCommercial() * (formData.tva_rate / 100);
    return Math.round(tva * 100) / 100;
  };

  // 5. Total net TTC = Net commercial + TVA
  const calculateTotalNet = () => {
    const total = calculateNetCommercial() + calculateTVAAmount();
    return Math.round(total * 100) / 100;
  };

  // 6. Total acompte deductions
  const calculateTotalAcompteDeductions = () => {
    return availableAcomptes
      .filter(a => selectedAcompteIds.includes(a.id))
      .reduce((sum, a) => sum + Number(a.total_net), 0);
  };

  // 7. Net à payer = TTC - acomptes
  const calculateNetAPayer = () => {
    const ttc = calculateTotalNet();
    const deductions = calculateTotalAcompteDeductions();
    return Math.round((ttc - deductions) * 100) / 100;
  };


  const handleSubmit = async () => {
    if (isAcompte) {
      // Acompte validations
      if (!selectedDevis) {
        showError('Veuillez sélectionner un devis');
        return;
      }

      const pct = typeof acomptePercentage === 'string'
        ? parseFloat(acomptePercentage.replace(',', '.'))
        : acomptePercentage;

      if (!pct || pct <= 0) {
        showError("Veuillez saisir un pourcentage d'acompte valide");
        return;
      }

      if (acompteInfo) {
        const maxAllowed = isEdit
          ? acompteInfo.remaining_percentage + (Number(acomptePercentage) || 0)
          : acompteInfo.remaining_percentage;
        if (pct > maxAllowed) {
          showError(`Le pourcentage dépasse le maximum disponible (${acompteInfo.remaining_percentage.toFixed(2)}%)`);
          return;
        }
      }

      const validItems = lineItems.filter(item => {
        const prix = typeof item.prix_unitaire === 'string'
          ? parseFloat(item.prix_unitaire.replace(',', '.'))
          : item.prix_unitaire;
        return item.designation && item.designation.trim() !== '' && prix && prix > 0;
      });

      if (validItems.length === 0) {
        showError('Veuillez ajouter au moins un article avec désignation et prix');
        return;
      }

      try {
        setLoading(true);

        const items: FactureItemCreate[] = validItems.map(item => {
          const prix = typeof item.prix_unitaire === 'string'
            ? parseFloat(item.prix_unitaire.replace(',', '.')) || 0
            : (item.prix_unitaire ?? 0);

          return {
            designation: item.designation,
            quantite: item.quantite,
            prix_unitaire: Math.round(prix * 100) / 100,
            total: Math.round(item.quantite * prix * 100) / 100,
          };
        });

        const remise = typeof formData.remise === 'string'
          ? parseFloat(formData.remise.replace(',', '.')) || 0
          : (formData.remise ?? 0);

        if (isEdit && factureId) {
          const updateData: FactureUpdate = {
            date: formData.date,
            client_id: selectedDevis.client_id,
            devis_id: selectedDevis.id,
            acompte_percentage: pct,
            bc_numero: acompteBcNumero || undefined,
            remarque: formData.remarque || undefined,
            tva_rate: formData.tva_rate,
            remise: Math.round(remise * 100) / 100,
            items,
          };
          await facturesAPI.update(factureId, updateData);
          showSuccess("Facture d'acompte modifiée avec succès");
        } else {
          const createData: FactureCreate = {
            date: formData.date,
            client_id: selectedDevis.client_id,
            type: 'acompte',
            devis_id: selectedDevis.id,
            acompte_percentage: pct,
            bc_numero: acompteBcNumero || undefined,
            remarque: formData.remarque || undefined,
            tva_rate: formData.tva_rate,
            remise: Math.round(remise * 100) / 100,
            items,
          };
          await facturesAPI.create(createData);
          showSuccess("Facture d'acompte créée avec succès");
        }

        clearDraft();
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1000);
      } catch (err: any) {
        showError(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    } else {
      // Normal facture submit
      if (!selectedClient) {
        showError('Veuillez sélectionner un client');
        return;
      }

      if (selectedBLs.length === 0) {
        showError('Veuillez sélectionner au moins un bon de livraison');
        return;
      }

      const validItems = lineItems.filter(item => {
        const prix = typeof item.prix_unitaire === 'string'
          ? parseFloat(item.prix_unitaire.replace(',', '.'))
          : item.prix_unitaire;
        return prix && prix > 0;
      });

      if (validItems.length === 0) {
        showError('Veuillez saisir le prix unitaire pour au moins un article');
        return;
      }

      try {
        setLoading(true);

        const items: FactureItemCreate[] = validItems.map(item => {
          const prix = typeof item.prix_unitaire === 'string'
            ? parseFloat(item.prix_unitaire.replace(',', '.')) || 0
            : (item.prix_unitaire ?? 0);

          return {
            bon_livraison_item_id: item.bon_livraison_item_id,
            prix_unitaire: Math.round(prix * 100) / 100,
            total: Math.round(item.quantite * prix * 100) / 100,
          };
        });

        const remise = typeof formData.remise === 'string'
          ? parseFloat(formData.remise.replace(',', '.')) || 0
          : (formData.remise ?? 0);

        if (isEdit && factureId) {
          const updateData: FactureUpdate = {
            date: formData.date,
            client_id: selectedClient.id,
            remarque: formData.remarque || undefined,
            tva_rate: formData.tva_rate,
            remise: Math.round(remise * 100) / 100,
            items,
            acompte_deduction_ids: selectedAcompteIds.length > 0 ? selectedAcompteIds : [],
          };
          await facturesAPI.update(factureId, updateData);
          showSuccess('Facture modifiée avec succès');
        } else {
          const createData: FactureCreate = {
            date: formData.date,
            client_id: selectedClient.id,
            type: 'normale',
            remarque: formData.remarque || undefined,
            tva_rate: formData.tva_rate,
            remise: Math.round(remise * 100) / 100,
            items,
            acompte_deduction_ids: selectedAcompteIds.length > 0 ? selectedAcompteIds : undefined,
          };
          await facturesAPI.create(createData);
          showSuccess('Facture créée avec succès');
        }

        clearDraft();
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1000);
      } catch (err: any) {
        showError(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    }
  };

  // X / ANNULER = deliberate cancel: wipe inputs and discard any draft.
  const handleClose = () => {
    clearDraft();
    resetForm();
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      saveDraft({
        formData,
        selectedClient,
        selectedBLs,
        selectedAcompteIds,
        selectedDevis,
        acomptePercentage,
        acompteBcNumero,
        lineItems,
      });
      onClose();
    }
  };

  const dialogTitle = isAcompte
    ? (isEdit ? "Modifier la Facture d'Acompte" : "Nouvelle Facture d'Acompte")
    : (isEdit ? 'Modifier la Facture' : 'Nouvelle Facture');

  return (
    <>
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
        TransitionComponent={SlideUpTransition}
        PaperProps={{ sx: { minHeight: '80vh' } }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 600 }}>
                {dialogTitle}
              </Typography>
              {isAcompte && (
                <Chip label="Acompte" size="small" color="info" />
              )}
            </Box>
            <Box display="flex" alignItems="center">
              <MinimizeButton color="#4CAF50" onClick={minimize} />
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Header Section */}
          <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
            Informations Générales
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Date"
                type="date"
                fullWidth
                size="small"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {isAcompte ? (
              <>
                <Grid item xs={12} sm={6} md={5}>
                  <Autocomplete
                    options={devisList}
                    getOptionLabel={(option) => `${option.reference} - ${option.client_name || ''}`}
                    value={selectedDevis}
                    onChange={(_, value) => handleDevisChange(value)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField {...params} label="Devis" required size="small" />
                    )}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="BC N°"
                    size="small"
                    fullWidth
                    value={acompteBcNumero}
                    onChange={(e) => setAcompteBcNumero(e.target.value)}
                    placeholder="N° Bon de Commande"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Pourcentage d'acompte (%)"
                    type="text"
                    size="small"
                    fullWidth
                    value={acomptePercentage}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d*[.,]?\d*$/.test(value)) {
                        setAcomptePercentage(value);
                      }
                    }}
                    onBlur={() => {
                      if (acomptePercentage && acomptePercentage !== '') {
                        const numVal = typeof acomptePercentage === 'string'
                          ? parseFloat(acomptePercentage.replace(',', '.'))
                          : acomptePercentage;
                        if (!isNaN(numVal)) {
                          const clamped = Math.min(100, Math.max(0, numVal));
                          setAcomptePercentage(clamped);
                          // Recalculate items from original devis prices × percentage
                          if (selectedDevis && clamped > 0) {
                            setLineItems(buildAcompteItems(selectedDevis, clamped));
                          }
                        }
                      }
                    }}
                    helperText={
                      acompteInfo
                        ? `Restant: ${acompteInfo.remaining_percentage.toFixed(2)}% | Utilisé: ${acompteInfo.total_percentage_used.toFixed(2)}%`
                        : ''
                    }
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6} md={5}>
                  <Autocomplete
                    options={clients}
                    getOptionLabel={(option) => `${option.code} - ${option.name}`}
                    value={selectedClient}
                    onChange={(_, value) => handleClientChange(value)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField {...params} label="Client" required size="small" />
                    )}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    key={selectedBLs.length}
                    options={availableBLs.filter(bl => !selectedBLs.find(s => s.id === bl.id))}
                    getOptionLabel={(option) => `${option.reference} - ${new Date(option.date).toLocaleDateString('fr-FR')}`}
                    onChange={(_, value) => handleAddBL(value)}
                    value={null}
                    disabled={!selectedClient}
                    blurOnSelect
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Ajouter un BL"
                        size="small"
                        helperText={!selectedClient ? "Sélectionnez un client d'abord" : ""}
                      />
                    )}
                    size="small"
                  />
                </Grid>
              </>
            )}
          </Grid>

          {/* Plafond Warning (only for normale) */}
          {!isAcompte && selectedClient && isPlafondDepasse && !isEdit && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                borderRadius: 1,
                border: '1px solid #F44336',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <WarningIcon sx={{ color: '#F44336', fontSize: 32 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ color: '#F44336', fontWeight: 600 }}>
                  {clientSummary?.plafond_fixe === 0 ? 'Aucun crédit autorisé !' : 'Plafond de crédit dépassé !'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {clientSummary?.plafond_fixe === 0 ? (
                    <>Ce client n'a pas de plafond de crédit autorisé. Impossible de créer une nouvelle facture.</>
                  ) : (
                    <>
                      Ce client a dépassé son plafond de crédit de{' '}
                      <strong style={{ color: '#F44336' }}>
                        {clientSummary?.plafond_depasse.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                      </strong>
                      . Impossible de créer une nouvelle facture.
                    </>
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Plafond fixé: {clientSummary?.plafond_fixe.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH |
                  Total impayé: {clientSummary?.total_impayee.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                </Typography>
              </Box>
            </Box>
          )}

          {/* Acompte Info Box */}
          {isAcompte && acompteInfo && acompteInfo.acompte_factures.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: 'rgba(33, 150, 243, 0.08)',
                borderRadius: 1,
                border: '1px solid rgba(33, 150, 243, 0.3)',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: '#1976D2', mb: 1, fontWeight: 600 }}>
                Factures d'acompte existantes pour ce devis
              </Typography>
              {acompteInfo.acompte_factures.map((af) => (
                <Typography key={af.id} variant="body2" color="text.secondary">
                  {af.reference} — {af.acompte_percentage}% — {Number(af.total_net).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH ({af.status})
                </Typography>
              ))}
            </Box>
          )}

          {/* Client & BL/Devis Info */}
          {selectedClient && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: 'rgba(76, 175, 80, 0.08)',
                borderRadius: 1,
                border: '1px solid rgba(76, 175, 80, 0.3)',
              }}
            >
              <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
                {/* Client Info */}
                <Box flex={1}>
                  <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1.5, fontWeight: 600 }}>
                    Informations du Client
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PersonIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                      <Typography variant="body2">
                        <strong>{selectedClient.name || 'N/A'}</strong>
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <IceIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                      <Typography variant="body2" color="text.secondary">
                        ICE: {selectedClient.ice || 'N/A'}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                      <Typography variant="body2" color="text.secondary">
                        {selectedClient.address || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Vertical Divider */}
                <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, borderColor: 'rgba(76, 175, 80, 0.3)' }} />
                <Divider sx={{ display: { xs: 'block', md: 'none' }, borderColor: 'rgba(76, 175, 80, 0.3)' }} />

                {/* Right side: BLs or Devis info */}
                <Box flex={1}>
                  {isAcompte ? (
                    <>
                      <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1.5, fontWeight: 600 }}>
                        Devis Sélectionné
                      </Typography>
                      {selectedDevis ? (
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Typography variant="body2">
                            <strong>{selectedDevis.reference}</strong>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Montant: {Number(selectedDevis.montant_total).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Date: {new Date(selectedDevis.date).toLocaleDateString('fr-FR')}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Aucun devis sélectionné
                        </Typography>
                      )}
                    </>
                  ) : (
                    <>
                      <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1.5, fontWeight: 600 }}>
                        Bons de Livraison Sélectionnés
                      </Typography>
                      {selectedBLs.length > 0 ? (
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          {selectedBLs.map(bl => (
                            <Box key={bl.id} display="flex" alignItems="center" justifyContent="space-between">
                              <Typography variant="body2">
                                <strong>{bl.reference}</strong>
                                {bl.bon_commande_number && (
                                  <Typography component="span" variant="body2" color="text.secondary">
                                    {' '}— BC: {bl.bon_commande_number}
                                  </Typography>
                                )}
                              </Typography>
                              <IconButton size="small" onClick={() => handleRemoveBL(bl.id)} sx={{ p: 0.5 }}>
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Aucun BL sélectionné
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Items Table */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1" sx={{ color: '#4CAF50', fontWeight: 600 }}>
              Articles
            </Typography>
            {isAcompte && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddAcompteItem}
                sx={{ color: '#4CAF50' }}
              >
                Ajouter une ligne
              </Button>
            )}
          </Box>

          {lineItems.length > 0 ? (
            <TableContainer sx={{ mb: 3 }}>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'background.default' }}>
                    <TableCell sx={{ width: isAcompte ? '35%' : '40%' }}>Désignation</TableCell>
                    <TableCell sx={{ width: '15%' }} align="right">Quantité</TableCell>
                    <TableCell sx={{ width: '20%' }} align="right">Prix Unitaire</TableCell>
                    <TableCell sx={{ width: '20%' }} align="right">Total</TableCell>
                    {isAcompte && <TableCell sx={{ width: '10%' }} align="center" />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {isAcompte ? (
                          <TextField
                            size="small"
                            value={item.designation}
                            onChange={(e) => handleAcompteItemChange(index, 'designation', e.target.value)}
                            fullWidth
                            placeholder="Désignation..."
                          />
                        ) : (
                          <Box sx={{ maxHeight: 80, overflowY: 'auto', wordBreak: 'break-word' }}>
                            {item.designation}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isAcompte ? (
                          <TextField
                            type="text"
                            size="small"
                            value={item.quantite}
                            onChange={(e) => handleAcompteItemChange(index, 'quantite', e.target.value)}
                            fullWidth
                            placeholder="1"
                          />
                        ) : (
                          item.quantite
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="text"
                          size="small"
                          value={item.prix_unitaire ?? ''}
                          onChange={(e) => handlePrixUnitaireChange(index, e.target.value)}
                          onBlur={() => handlePrixUnitaireBlur(index)}
                          fullWidth
                          placeholder="0.00"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {item.total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                      </TableCell>
                      {isAcompte && (
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleRemoveAcompteItem(index)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center', backgroundColor: 'background.default', borderRadius: 1, mb: 3 }}>
              <Typography color="text.secondary">
                {isAcompte
                  ? "Cliquez sur \"Ajouter une ligne\" pour ajouter des articles"
                  : "Sélectionnez un ou plusieurs bons de livraison pour afficher les articles"
                }
              </Typography>
            </Box>
          )}

          {/* Remarque */}
          <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
            Remarque
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            value={formData.remarque}
            onChange={(e) => setFormData({ ...formData, remarque: e.target.value })}
            placeholder="Remarques ou conditions..."
            sx={{ mb: 3 }}
          />

          <Divider sx={{ my: 2 }} />

          {/* Totals */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
                Paramètres
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>TVA</InputLabel>
                    <Select
                      value={formData.tva_rate}
                      label="TVA"
                      onChange={(e) => setFormData({ ...formData, tva_rate: Number(e.target.value) })}
                    >
                      {TVA_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Remise (%)"
                    type="text"
                    size="small"
                    fullWidth
                    value={formData.remise ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d*[.,]?\d*$/.test(value)) {
                        setFormData({ ...formData, remise: value === '' ? null : value });
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value && value !== '') {
                        const numValue = parseFloat(value.replace(',', '.'));
                        if (!isNaN(numValue)) {
                          const clamped = Math.min(100, Math.max(0, numValue));
                          setFormData({ ...formData, remise: clamped });
                        }
                      }
                    }}
                  />
                </Grid>
                {!isAcompte && availableAcomptes.length > 0 && (
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={availableAcomptes}
                      getOptionLabel={(option) =>
                        `${option.reference}${option.devis_reference ? ` — ${option.devis_reference}` : ''} (${option.acompte_percentage}%) — ${Number(option.total_net).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH`
                      }
                      value={availableAcomptes.filter(a => selectedAcompteIds.includes(a.id))}
                      onChange={(_, values) => setSelectedAcompteIds(values.map(v => v.id))}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Factures d'acompte à déduire"
                          size="small"
                          placeholder="Sélectionner..."
                        />
                      )}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            label={`${option.reference} — ${Number(option.total_net).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH`}
                            size="small"
                            sx={{ backgroundColor: 'rgba(156, 39, 176, 0.1)', color: '#9C27B0', borderColor: '#9C27B0' }}
                            variant="outlined"
                          />
                        ))
                      }
                      size="small"
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
                Totaux
              </Typography>
              <Box sx={{ backgroundColor: 'background.default', p: 2, borderRadius: 1 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Total hors taxes:</Typography>
                  <Typography variant="body2">
                    {calculateTotalHT().toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Remise ({typeof formData.remise === 'string' ? parseFloat(formData.remise.replace(',', '.')) || 0 : (formData.remise ?? 0)}%):</Typography>
                  <Typography variant="body2">
                    -{calculateRemiseAmount().toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Net commercial:</Typography>
                  <Typography variant="body2">
                    {calculateNetCommercial().toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">T.V.A ({formData.tva_rate}%):</Typography>
                  <Typography variant="body2">
                    {calculateTVAAmount().toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mt={2} pt={2} borderTop="2px solid #4CAF50">
                  <Typography variant="subtitle1" fontWeight={600}>Total T.T.C:</Typography>
                  <Typography variant="subtitle1" fontWeight={600} color="#4CAF50">
                    {calculateTotalNet().toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                  </Typography>
                </Box>
                {!isAcompte && selectedAcompteIds.length > 0 && (
                  <>
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed rgba(156, 39, 176, 0.4)' }}>
                      {availableAcomptes
                        .filter(a => selectedAcompteIds.includes(a.id))
                        .map(a => (
                          <Box key={a.id} display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2" sx={{ color: '#9C27B0' }}>
                              Acompte {a.reference}:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#9C27B0' }}>
                              -{Number(a.total_net).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                            </Typography>
                          </Box>
                        ))}
                    </Box>
                    <Box display="flex" justifyContent="space-between" mt={1.5} pt={1.5} borderTop="2px solid #9C27B0">
                      <Typography variant="subtitle1" fontWeight={600}>Net à payer:</Typography>
                      <Typography variant="subtitle1" fontWeight={600} color="#9C27B0">
                        {calculateNetAPayer().toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} variant="outlined">
            Annuler
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading || (!isAcompte && !isEdit && isPlafondDepasse)}
            sx={{
              backgroundColor: '#4CAF50',
              '&:hover': { backgroundColor: '#388E3C' },
            }}
          >
            {loading ? 'Sauvegarde...' : isEdit ? 'Modifier' : 'Valider'}
          </Button>
        </DialogActions>
      </Dialog>

      <MinimizedBar
        open={open && minimized}
        color="#4CAF50"
        title={dialogTitle}
        onRestore={restore}
        onClose={handleClose}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </>
  );
};

export default FactureFormDialog;
